// hooks/useSendMessage.js

import { useState, useCallback } from "react";
import { saveMessage } from "../db/fsDB";
import { loadGroupKey } from "../utils/Cryptoutils";
import { fetchAndCachePublicKey } from "../utils/pbKey";
import {
  encryptDualDm,
  encryptGroup,
  encryptDmReplyPreview,
  encryptGroupReplyPreview,
} from "../utils/encrypt";

/* ─────────────────────────────────────────────
   MODULE-LEVEL HELPERS
───────────────────────────────────────────── */

/**
 * Derives the optimistic/display media type from a MIME type string.
 * Pure synchronous — no await needed.
 */
const resolveMediaType = (mimeType) => {
  if (!mimeType) return "document";
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  return "document";
};

/**
 * Builds a short plaintext reply preview from a reply object.
 * Synchronous — returns a plain value, not a Promise.
 * Callers must NOT await this.
 */
const buildReplyPreview = (reply) => {
  if (!reply) return null;

  if (reply.text) {
    return { type: "text", value: reply.text.slice(0, 80) };
  }

  if (reply.media) {
    return {
      type: "media",
      value: {
        url: reply.media.url ?? null,
        mimeType: reply.media.mimeType ?? null,
        fileName: reply.media.fileName ?? null,
      },
    };
  }

  return null;
};

/**
 * Safely revoke a blob: URL. No-ops for anything else (remote URLs, null, etc).
 */
const safeRevokeObjectURL = (url) => {
  if (!url || typeof url !== "string" || !url.startsWith("blob:")) return;
  try {
    URL.revokeObjectURL(url);
  } catch (_e) {
    /* ignore */
  }
};

/* ─────────────────────────────────────────────
   HOOK
───────────────────────────────────────────── */
export const useSendMessage = ({
  socket,
  myChatId,
  selectedItem,
  setMessages,
  setUsers,
  setGroups,
  input,
  setInput,
  selectedReply,
  setSelectedReply,
  pendingMedia,
  setPendingMedia,
  voiceMediaRef,
  uploadMedia,
  clearPendingMedia,
  setIsUploading,
  setUploadProgress,
  groupMembers,
  getUserKey,
  getGroupKey,
  cryptoFns,
  showToast,
  scrollToBottom,
  uuid,
}) => {
  const [isSending, setIsSending] = useState(false);

  /* ─────────────────────────────────────────────────────────────
     LOCAL PREVIEW UPDATER
     Synchronous — updates users/groups list with last message text.
  ───────────────────────────────────────────────────────────── */
  const updateLocalPreview = useCallback(
    (chatKey, previewText, now) => {
      if (!chatKey) return;

      if (selectedItem?.type === "user") {
        setUsers((prev) =>
          prev.map((u) =>
            String(u.chatId) === String(selectedItem.chatId)
              ? { ...u, lastMessage: previewText, lastActivityAt: now }
              : u
          )
        );
      }

      if (selectedItem?.type === "group") {
        setGroups((prev) =>
          prev.map((g) =>
            String(g.groupId) === String(selectedItem.groupId)
              ? { ...g, lastMessage: previewText, lastActivityAt: now }
              : g
          )
        );
      }
    },
    [selectedItem, setUsers, setGroups]
  );

  /* ─────────────────────────────────────────────────────────────
     BUILD DM REPLY PAYLOAD
     Encrypts a reply preview for both recipient and sender envelopes.
  ───────────────────────────────────────────────────────────── */
  const buildDmReplyPayload = useCallback(
    async (reply, recipientPublicKey, senderPublicKey) => {
      if (!reply?.msgId) return null;

      // buildReplyPreview is synchronous — no await
      const preview = buildReplyPreview(reply);

      const { replyPreviewEncrypted, replyPreviewEncryptedSender } =
        await encryptDmReplyPreview(preview, recipientPublicKey, senderPublicKey);

      return {
        msgId: reply.msgId,
        replyPreviewEncrypted,
        replyPreviewEncryptedSender,
      };
    },
    []
  );

  /* ─────────────────────────────────────────────────────────────
     BUILD GROUP REPLY PAYLOAD
     Encrypts a reply preview with the shared group key.
  ───────────────────────────────────────────────────────────── */
  const buildGroupReplyPayload = useCallback(
    async (reply, groupKey) => {
      if (!reply?.msgId) return null;

      const previewText = reply.text?.slice(0, 80) ?? "";
      const replyPreviewEncrypted = await encryptGroupReplyPreview(
        previewText,
        groupKey
      );

      return { msgId: reply.msgId, replyPreviewEncrypted };
    },
    []
  );

  /* ─────────────────────────────────────────────────────────────
     SEND MESSAGE
  ───────────────────────────────────────────────────────────── */
   const sendMessage = useCallback(
    async (textOrFile, mediaOverride) => {
      if (!socket || !selectedItem) return;

      const mediaSnapshot = mediaOverride ?? pendingMedia ?? voiceMediaRef?.current ?? null;
      // buildReplyPreview is synchronous — call directly, no await
      const replyPreview = buildReplyPreview(selectedReply);
      const isMediaSend =
        textOrFile instanceof File || textOrFile instanceof Blob;
      const textToSend = isMediaSend
        ? null
        : typeof textOrFile === "string"
        ? textOrFile.trim()
        : (input ?? "").trim();

      if (!isMediaSend && !textToSend) return;

      const msgId = uuid();
      const now = Date.now();

      const isVoice =
        mediaSnapshot?.isVoice === true ||
        Boolean(mediaSnapshot?.mimeType?.startsWith("audio/"));

      // ── The optimistic bubble (below) is rendered using
      // mediaSnapshot.previewUrl (a blob: URL). We must NOT revoke that
      // URL until the optimistic record has been replaced by the real,
      // uploaded media URL — otherwise the sender's own bubble briefly
      // shows a broken-image icon instead of the picture/video they just
      // sent. `finishedWithPreviewUrl` tracks whether it's safe to revoke.
      let previewUrlToRevoke = isMediaSend ? mediaSnapshot?.previewUrl ?? null : null;

      // Clears the pendingMedia UI state WITHOUT revoking the blob URL,
      // since the optimistic message bubble is still using it.
      const resetPendingMediaUIOnly = () => {
        setPendingMedia?.(null);
        setUploadProgress?.(0);
      };

      try {
        setIsSending(true);

        /* ════════════════════════════════════════════════════
           GROUP SEND
        ════════════════════════════════════════════════════ */
        if (selectedItem.type === "group") {
          const groupId = selectedItem.groupId;
          const chatKey = getGroupKey(groupId);
          const messageType = isMediaSend ? "media" : "text";

          // ── STEP 1 — Optimistic UI ──────────────────────────
          const optimisticMedia =
            isMediaSend && mediaSnapshot
              ? {
                  url: mediaSnapshot.previewUrl ?? null,
                  type: resolveMediaType(mediaSnapshot.mimeType),
                  mimeType: mediaSnapshot.mimeType ?? null,
                  fileName: mediaSnapshot.fileName ?? null,
                  fileSize: mediaSnapshot.fileSize ?? null,
                  isVoice,
                  _optimistic: true,
                }
              : null;

          const optimisticRecord = {
            id: msgId,
            chatKey,
            timestamp: now,
            messageType,
            text: textToSend ?? null,
            media: optimisticMedia,
            replyToMessageId: selectedReply?.msgId ?? null,
            replyPreview,
            isGroup: true,
            groupId,
            fromMe: true,
            senderChatId: myChatId,
            _optimistic: true,
          };

          setMessages((prev) => ({
            ...prev,
            [chatKey]: [...(prev[chatKey] ?? []), optimisticRecord],
          }));

          updateLocalPreview(
            chatKey,
            textToSend ?? (isMediaSend ? "📎 Media" : ""),
            now
          );

          // ── STEP 2 — UI cleanup (keep the blob URL alive!) ──
          setInput?.("");
          setSelectedReply?.(null);
          resetPendingMediaUIOnly();
          scrollToBottom?.();

          // ── STEP 3 — Encrypt ────────────────────────────────
          const groupKey = await loadGroupKey(groupId);
          if (!groupKey) {
            showToast?.("Encryption key unavailable. Please try again.", "error");
            setMessages((prev) => ({
              ...prev,
              [chatKey]: (prev[chatKey] ?? []).filter((m) => m.id !== msgId),
            }));
            safeRevokeObjectURL(previewUrlToRevoke);
            return;
          }

          let encryptedText = null;
          let encryptedMedia = null;
          let uploadedMedia = null;

          if (textToSend) {
            const enc = await encryptGroup(textToSend, groupKey);
            if (!enc) {
              showToast?.("Failed to encrypt message.", "error");
              setMessages((prev) => ({
                ...prev,
                [chatKey]: (prev[chatKey] ?? []).filter((m) => m.id !== msgId),
              }));
              safeRevokeObjectURL(previewUrlToRevoke);
              return;
            }
            encryptedText = { ...enc, ephemeralPublicKey: null };
          }

          if (isMediaSend) {
            setIsUploading?.(true);
            try {
             uploadedMedia = await uploadMedia(
  textOrFile,
  msgId,
  "group-message",
  (p) => setUploadProgress?.(p)
);
            } finally {
              setIsUploading?.(false);
              setUploadProgress?.(0);
            }

            if (uploadedMedia) {
              const enc = await encryptGroup(
                JSON.stringify(uploadedMedia),
                groupKey
              );
              if (enc) encryptedMedia = { ...enc, ephemeralPublicKey: null };
            }
          }

          if (!encryptedText && !encryptedMedia) {
            setMessages((prev) => ({
              ...prev,
              [chatKey]: (prev[chatKey] ?? []).filter((m) => m.id !== msgId),
            }));
            safeRevokeObjectURL(previewUrlToRevoke);
            return;
          }

          const replyPayload = await buildGroupReplyPayload(
            selectedReply,
            groupKey
          );

          const resolvedMedia =
  isMediaSend && uploadedMedia
    ? {
        url: uploadedMedia?.url ?? null,

        objectKey:
          uploadedMedia?.objectKey ?? null,

        encrypted:
          uploadedMedia?.encrypted ?? false,

        type:
          resolveMediaType(
            mediaSnapshot?.mimeType
          ),

        mimeType:
          mediaSnapshot?.mimeType ?? null,

        fileName:
          mediaSnapshot?.fileName ?? null,

        fileSize:
          mediaSnapshot?.fileSize ?? null,

        isVoice,
      }
    : null;

          const finalRecord = {
            id: msgId,
            chatKey,
            timestamp: now,
            messageType,
            text: textToSend ?? null,
            media: resolvedMedia,
            encryptedText,
            encryptedMedia,
            replyToMessageId: replyPayload?.msgId ?? null,
            replyPreviewEncrypted: replyPayload?.replyPreviewEncrypted ?? null,
            replyPreview,
            isGroup: true,
            groupId,
            fromMe: true,
            senderChatId: myChatId,
          };

          // ── STEP 4 — Save encrypted message to IndexedDB FIRST ──
          const forIDB = {
            ...finalRecord,
            timestamp: Number(finalRecord.timestamp),
          };

          // Store encrypted content, not the plaintext display fields.
          delete forIDB.media;
          delete forIDB.text;

          try {
            await saveMessage(forIDB);
          } catch (_err) {
            showToast?.(
              "Message was not sent because it could not be saved locally. Please try again.",
              "error"
            );

            setMessages((prev) => ({
              ...prev,
              [chatKey]: (prev[chatKey] ?? []).filter(
                (m) => m.id !== msgId
              ),
            }));

            safeRevokeObjectURL(previewUrlToRevoke);
            return;
          }

          // ── STEP 5 — Emit only after local persistence succeeds ──
          socket.emit("group-send-message", {
            groupId,
            msgId,
            ...(encryptedText ? { encryptedText } : {}),
            ...(encryptedMedia ? { encryptedMedia } : {}),
            ...(replyPayload ? { reply: replyPayload } : {}),
          });

          // ── STEP 6 — Replace optimistic record ──────────────
          setMessages((prev) => {
            const list = prev[chatKey] ?? [];
            return {
              ...prev,
              [chatKey]: list.map((m) =>
                m.id === msgId ? finalRecord : m
              ),
            };
          });

          safeRevokeObjectURL(previewUrlToRevoke);
        }

        /* ════════════════════════════════════════════════════
           DM SEND
        ════════════════════════════════════════════════════ */
        if (selectedItem.type === "user") {
          const chatKey = getUserKey(selectedItem.chatId);
          const messageType = isMediaSend ? "media" : "text";

          // ── STEP 1 — Optimistic UI ──────────────────────────
          const optimisticMedia =
            isMediaSend && mediaSnapshot
              ? {
                  url: mediaSnapshot.previewUrl ?? null,
                  type: resolveMediaType(mediaSnapshot.mimeType),
                  mimeType: mediaSnapshot.mimeType ?? null,
                  fileName: mediaSnapshot.fileName ?? null,
                  fileSize: mediaSnapshot.fileSize ?? null,
                  isVoice,
                  _optimistic: true,
                }
              : null;

          const optimisticRecord = {
            id: msgId,
            chatKey,
            timestamp: now,
            messageType,
            text: textToSend ?? null,
            media: optimisticMedia,
            replyToMessageId: selectedReply?.msgId ?? null,
            replyPreview,
            fromMe: true,
            senderChatId: myChatId,
            _optimistic: true,
          };

          setMessages((prev) => ({
            ...prev,
            [chatKey]: [...(prev[chatKey] ?? []), optimisticRecord],
          }));

          updateLocalPreview(
            chatKey,
            textToSend ?? (isMediaSend ? "📎 Media" : ""),
            now
          );

          // ── STEP 2 — UI cleanup (keep the blob URL alive!) ──
          setInput?.("");
          setSelectedReply?.(null);
          resetPendingMediaUIOnly();
          scrollToBottom?.();

          // ── STEP 3 — Fetch public keys ──────────────────────
          const [recipientPublicKey, senderPublicKey] = await Promise.all([
            fetchAndCachePublicKey(selectedItem.chatId, socket),
            fetchAndCachePublicKey(myChatId, socket),
          ]);

          if (!recipientPublicKey || !senderPublicKey) {
            showToast?.("Encryption keys unavailable.", "error");
            setMessages((prev) => ({
              ...prev,
              [chatKey]: (prev[chatKey] ?? []).filter((m) => m.id !== msgId),
            }));
            safeRevokeObjectURL(previewUrlToRevoke);
            return;
          }

          let encryptedText = null;
          let encryptedTextRecipient = null;
          let encryptedMedia = null;
          let encryptedMediaRecipient = null;
          let uploadedMedia = null;

          // TEXT — dual encrypt
          if (textToSend) {
            const result = await encryptDualDm(
              textToSend,
              recipientPublicKey,
              senderPublicKey
            );
            if (!result) {
              showToast?.("Failed to encrypt message.", "error");
              setMessages((prev) => ({
                ...prev,
                [chatKey]: (prev[chatKey] ?? []).filter((m) => m.id !== msgId),
              }));
              safeRevokeObjectURL(previewUrlToRevoke);
              return;
            }
            encryptedTextRecipient = result.forRecipient;
            encryptedText = result.forSender;
          }

          // MEDIA — upload then dual encrypt
          if (isMediaSend) {
            setIsUploading?.(true);
            try {
             uploadedMedia = await uploadMedia(
  textOrFile,
  msgId,
  "message",
  (p) => setUploadProgress?.(p)
);
            } finally {
              setIsUploading?.(false);
              setUploadProgress?.(0);
            }

            if (uploadedMedia) {
              const result = await encryptDualDm(
                JSON.stringify(uploadedMedia),
                recipientPublicKey,
                senderPublicKey
              );
              if (!result) {
                showToast?.("Media encryption failed.", "error");
                setMessages((prev) => ({
                  ...prev,
                  [chatKey]: (prev[chatKey] ?? []).filter(
                    (m) => m.id !== msgId
                  ),
                }));
                safeRevokeObjectURL(previewUrlToRevoke);
                return;
              }
              encryptedMediaRecipient = result.forRecipient;
              encryptedMedia = result.forSender;
            }

            if (!encryptedMediaRecipient) {
              showToast?.("Media encryption failed.", "error");
              setMessages((prev) => ({
                ...prev,
                [chatKey]: (prev[chatKey] ?? []).filter((m) => m.id !== msgId),
              }));
              safeRevokeObjectURL(previewUrlToRevoke);
              return;
            }
          }

          const replyPayload = await buildDmReplyPayload(
            selectedReply,
            recipientPublicKey,
            senderPublicKey
          );

       const resolvedMedia =
  isMediaSend && uploadedMedia
    ? {
        url: uploadedMedia?.url ?? null,

        objectKey:
          uploadedMedia?.objectKey ?? null,

        encrypted:
          uploadedMedia?.encrypted ?? false,

        type:
          resolveMediaType(
            mediaSnapshot?.mimeType
          ),

        mimeType:
          mediaSnapshot?.mimeType ?? null,

        fileName:
          mediaSnapshot?.fileName ?? null,

        fileSize:
          mediaSnapshot?.fileSize ?? null,

        isVoice,
      }
    : null;

          const finalRecord = {
            id: msgId,
            chatKey,
            timestamp: now,
            messageType,
            text: textToSend ?? null,
            media: resolvedMedia,
            encryptedText,
            encryptedMedia,
            replyToMessageId: replyPayload?.msgId ?? null,
            replyPreviewEncrypted:
              replyPayload?.replyPreviewEncryptedSender ?? null,
            replyPreview,
            fromMe: true,
            senderChatId: myChatId,
          };

          // ── STEP 4 — Save sender's encrypted copy to IndexedDB FIRST ──
          const forIDB = {
            id: msgId,
            chatKey,
            timestamp: Number(now),
            messageType,
            encryptedText,
            encryptedMedia,
            replyToMessageId: replyPayload?.msgId ?? null,
            replyPreviewEncrypted:
              replyPayload?.replyPreviewEncryptedSender ?? null,
            fromMe: true,
            senderChatId: myChatId,
            recipientChatId: selectedItem.chatId,
          };

          try {
            await saveMessage(forIDB);
          } catch (_err) {
            showToast?.(
              "Message was not sent because it could not be saved locally. Please try again.",
              "error"
            );

            setMessages((prev) => ({
              ...prev,
              [chatKey]: (prev[chatKey] ?? []).filter(
                (m) => m.id !== msgId
              ),
            }));

            safeRevokeObjectURL(previewUrlToRevoke);
            return;
          }

          // ── STEP 5 — Emit only after local persistence succeeds ──
          socket.emit("send-message", {
            recipientChatId: selectedItem.chatId,
            msgId,
            ...(encryptedTextRecipient && encryptedText
              ? {
                  encryptedText: {
                    forRecipient: encryptedTextRecipient,
                    forSender: encryptedText,
                  },
                }
              : {}),
            ...(encryptedMediaRecipient && encryptedMedia
              ? {
                  encryptedMedia: {
                    forRecipient: encryptedMediaRecipient,
                    forSender: encryptedMedia,
                  },
                }
              : {}),
            ...(replyPayload?.replyPreviewEncrypted &&
            replyPayload?.replyPreviewEncryptedSender
              ? {
                  reply: {
                    msgId: replyPayload.msgId,
                    replyPreviewEncrypted: replyPayload.replyPreviewEncrypted,
                    replyPreviewEncryptedSender:
                      replyPayload.replyPreviewEncryptedSender,
                  },
                }
              : {}),
          });

          // ── STEP 6 — Replace optimistic record ──────────────
          setMessages((prev) => {
            const list = prev[chatKey] ?? [];
            return {
              ...prev,
              [chatKey]: list.map((m) =>
                m.id === msgId ? finalRecord : m
              ),
            };
          });

          safeRevokeObjectURL(previewUrlToRevoke);
        }
      } catch (err) {
        showToast?.("Failed to send message.", "error");
        // Best-effort cleanup: if something threw before we got a chance
        // to revoke above, make sure we don't leak the blob URL forever.
        safeRevokeObjectURL(previewUrlToRevoke);
      } finally {
        setIsSending(false);
      }
    },
    [
      socket,
      selectedItem,
      myChatId,
      input,
      setInput,
      selectedReply,
      setSelectedReply,
      getUserKey,
      getGroupKey,
      buildDmReplyPayload,
      buildGroupReplyPayload,
      cryptoFns,
      uploadMedia,
      pendingMedia,
      voiceMediaRef,
      setPendingMedia,
      setIsUploading,
      setUploadProgress,
      showToast,
      scrollToBottom,
      uuid,
      updateLocalPreview,
    ]
  );

  return { sendMessage, isSending };
};